import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import type { CrestBrowserLaunchConfig } from './crest-auth-config.js';
import {
  CrestAuthClient, CrestAuthFailure, CrestPasswordChangeFailure, crestAuthFailureDiagnostic,
  type CrestSignedInPasswordScope,
} from './crest-auth-client.js';

export type CrestAuthStatus =
  | 'loading'
  | 'unauthenticated'
  | 'recovery'
  | 'recovery-error'
  | 'recovery-complete'
  | 'password-change-complete'
  | 'password-change-unconfirmed'
  | 'authenticated';

interface CrestAuthContextValue {
  readonly status: CrestAuthStatus;
  readonly sessionGeneration: number;
  readonly tenantId: string;
  readonly applicationName: string;
  readonly publicCompanyName: string;
  readonly publicLogoUrl: string | null;
  readonly publicHeroImageUrl: string | null;
  readonly publicBrandColor: string;
  readonly publicBrandStrongColor: string;
  readonly publicAttentionColor: string;
  readonly signInWithPassword: (email: string, password: string) => Promise<void>;
  readonly requestPasswordRecovery: (email: string) => Promise<void>;
  readonly updateRecoveredPassword: (password: string) => Promise<void>;
  readonly changeSignedInPassword: (
    input: Readonly<{ currentPassword: string; newPassword: string }>,
    scope: CrestSignedInPasswordScope,
  ) => Promise<void>;
  readonly cancelPasswordRecovery: () => void;
  readonly signOut: () => Promise<void>;
  readonly getAccessToken: () => Promise<string | null>;
}

/** Each mounted provider/login owns a lease; old async completions cannot replace it. */
export class CrestAuthProviderFence {
  private active = false;
  private generation = 0;

  public get revision(): number { return this.generation; }

  public activate(): () => boolean {
    this.active = true;
    this.generation += 1;
    return this.capture();
  }

  public deactivate(): void {
    this.active = false;
    this.generation += 1;
  }

  public begin(): () => boolean {
    if (!this.active) throw new CrestAuthFailure('session-changed');
    this.generation += 1;
    return this.capture();
  }

  public capture(): () => boolean {
    const generation = this.generation;
    return () => this.active && this.generation === generation;
  }
}

export function crestPasswordChangeFailureStatus(error: unknown): CrestAuthStatus | null {
  if (error instanceof CrestPasswordChangeFailure) {
    if (error.outcome === 'unconfirmed') return 'password-change-unconfirmed';
    // A changed-but-stale result must not present a success message to another view.
    if (error.outcome === 'changed') return 'unauthenticated';
  }
  if (error instanceof CrestAuthFailure && (
    error.stage === 'session-missing' || error.stage === 'session-changed' ||
    error.stage === 'password-change-session-changed'
  )) return 'unauthenticated';
  return null;
}

const CrestAuthContext = createContext<CrestAuthContextValue | null>(null);

export function CrestAuthProvider(props: { readonly config: CrestBrowserLaunchConfig; readonly children: ReactNode }) {
  const client = useMemo(() => new CrestAuthClient(props.config), [props.config]);
  const fence = useMemo(() => new CrestAuthProviderFence(), [client]);
  const currentClient = useRef(client);
  currentClient.current = client;
  const passwordOperation = useRef<(() => boolean) | null>(null);
  const [state, setState] = useState<{
    client: CrestAuthClient; status: CrestAuthStatus; generation: number;
  }>(() => ({ client, status: 'loading', generation: 0 }));
  // A replacement configuration never renders the previous client's authenticated state.
  const status: CrestAuthStatus = state.client === client ? state.status : 'loading';
  const statusRef = useRef({ client, status });
  statusRef.current = { client, status };
  const writeStatus = useCallback((next: CrestAuthStatus) => {
    if (currentClient.current !== client) return;
    statusRef.current = { client, status: next };
    setState({ client, status: next, generation: fence.revision });
  }, [client, fence]);

  useEffect(() => {
    document.title = props.config.applicationName;
  }, [props.config.applicationName]);

  useEffect(() => {
    const lease = fence.activate();
    const isCurrent = () => currentClient.current === client && lease();
    writeStatus('loading');
    void client.restore().then((result) => {
      if (!isCurrent()) return;
      if (result.status === 'recovery') writeStatus('recovery');
      else if (result.status === 'authenticated') writeStatus('authenticated');
      else writeStatus(result.recoveryAttempted ? 'recovery-error' : 'unauthenticated');
    }).catch(() => {
      if (isCurrent()) writeStatus('unauthenticated');
    });
    // Do not discard the client's in-flight restore: StrictMode reuses that
    // promise after the recovery callback has already been removed from the URL.
    return () => fence.deactivate();
  }, [client, fence, writeStatus]);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    const lease = fence.begin();
    await client.signInWithPassword(email, password);
    if (currentClient.current === client && lease()) writeStatus('authenticated');
  }, [client, fence, writeStatus]);
  const requestPasswordRecovery = useCallback(async (email: string) => {
    await client.requestPasswordRecovery(email);
  }, [client]);
  const updateRecoveredPassword = useCallback(async (password: string) => {
    const lease = fence.capture();
    const isCurrent = () => currentClient.current === client && lease();
    try {
      await client.updateRecoveredPassword(password);
      if (isCurrent()) writeStatus('recovery-complete');
    } catch (error) {
      if (isCurrent() && error instanceof CrestAuthFailure && (
        error.stage === 'recovery-session-missing' ||
        error.stage === 'recovery-session-expired' ||
        (error.stage === 'recovery-password-response' && (error.status === 401 || error.status === 403))
      )) writeStatus('recovery-error');
      throw error;
    }
  }, [client, fence, writeStatus]);
  const changeSignedInPassword = useCallback(async (
    input: Readonly<{ currentPassword: string; newPassword: string }>,
    scope: CrestSignedInPasswordScope,
  ) => {
    const lease = fence.capture();
    const providerIsCurrent = () => currentClient.current === client && lease();
    const isCurrent = () => {
      try {
        return providerIsCurrent() && statusRef.current.client === client &&
          statusRef.current.status === 'authenticated' && scope.isCurrent();
      } catch {
        return false;
      }
    };
    if (!isCurrent()) throw new CrestAuthFailure('password-change-session-changed');
    if (passwordOperation.current?.() === true) throw new CrestAuthFailure('password-change-busy');
    passwordOperation.current = providerIsCurrent;
    try {
      const result = await client.changeSignedInPassword(input, { ...scope, isCurrent });
      if (providerIsCurrent()) {
        writeStatus(isCurrent() ? 'password-change-complete' : 'unauthenticated');
        if (!result.temporarySessionClosed) {
          console.error('crest-auth:password-change-temporary-session-cleanup-unconfirmed');
        }
      }
    } catch (error) {
      // The client may have cleared the originating authority after an uncertain
      // PUT. Leave that account's app immediately, but never replace a new login.
      if (providerIsCurrent()) {
        const next = crestPasswordChangeFailureStatus(error);
        if (next !== null) writeStatus(next);
      }
      throw error;
    } finally {
      if (passwordOperation.current === providerIsCurrent) passwordOperation.current = null;
    }
  }, [client, fence, writeStatus]);
  const cancelPasswordRecovery = useCallback(() => {
    fence.begin();
    client.cancelPasswordRecovery();
    writeStatus('unauthenticated');
  }, [client, fence, writeStatus]);
  const signOut = useCallback(async () => {
    fence.begin();
    const pending = client.signOut(); // local authority is cleared synchronously
    writeStatus('unauthenticated');
    await pending;
  }, [client, fence, writeStatus]);
  const getAccessToken = useCallback(async () => {
    const lease = fence.capture();
    const token = await client.getAccessToken();
    if (currentClient.current !== client || !lease()) return null;
    // A late token request must not erase the distinct normal-change result.
    if (token === null && statusRef.current.status === 'authenticated' &&
        passwordOperation.current?.() !== true) writeStatus('unauthenticated');
    return token;
  }, [client, fence, writeStatus]);
  const value = useMemo<CrestAuthContextValue>(() => ({
    status,
    sessionGeneration: state.client === client ? state.generation : 0,
    tenantId: props.config.tenantId,
    applicationName: props.config.applicationName,
    publicCompanyName: props.config.publicCompanyName,
    publicLogoUrl: props.config.publicLogoUrl,
    publicHeroImageUrl: props.config.publicHeroImageUrl,
    publicBrandColor: props.config.publicBrandColor,
    publicBrandStrongColor: props.config.publicBrandStrongColor,
    publicAttentionColor: props.config.publicAttentionColor,
    signInWithPassword,
    requestPasswordRecovery,
    updateRecoveredPassword,
    changeSignedInPassword,
    cancelPasswordRecovery,
    signOut,
    getAccessToken,
  }), [client, getAccessToken, props.config.applicationName, props.config.publicAttentionColor,
    props.config.publicBrandColor, props.config.publicBrandStrongColor, props.config.publicCompanyName,
    props.config.publicHeroImageUrl, props.config.publicLogoUrl, props.config.tenantId,
    cancelPasswordRecovery, changeSignedInPassword, requestPasswordRecovery, signInWithPassword, signOut,
    state.client, state.generation, status, updateRecoveredPassword]);

  return <CrestAuthContext value={value}>{props.children}</CrestAuthContext>;
}

export function useCrestAuth(): CrestAuthContextValue {
  const context = useContext(CrestAuthContext);
  if (context === null) throw new Error('CrestAuthProvider is missing.');
  return context;
}

export function CrestSessionGate(props: { readonly children: ReactNode }) {
  const auth = useCrestAuth();
  return (
    <CrestSessionGateView
      status={auth.status}
      applicationName={auth.applicationName}
      publicCompanyName={auth.publicCompanyName}
      publicLogoUrl={auth.publicLogoUrl}
      publicHeroImageUrl={auth.publicHeroImageUrl}
      publicBrandColor={auth.publicBrandColor}
      publicBrandStrongColor={auth.publicBrandStrongColor}
      publicAttentionColor={auth.publicAttentionColor}
      signInWithPassword={auth.signInWithPassword}
      requestPasswordRecovery={auth.requestPasswordRecovery}
      updateRecoveredPassword={auth.updateRecoveredPassword}
      cancelPasswordRecovery={auth.cancelPasswordRecovery}
    >
      {props.children}
    </CrestSessionGateView>
  );
}

export function CrestSessionGateView(props: {
  readonly status: CrestAuthStatus;
  readonly applicationName?: string;
  readonly publicCompanyName?: string;
  readonly publicLogoUrl?: string | null;
  readonly publicHeroImageUrl?: string | null;
  readonly publicBrandColor?: string;
  readonly publicBrandStrongColor?: string;
  readonly publicAttentionColor?: string;
  readonly signInWithPassword: (email: string, password: string) => Promise<void>;
  readonly requestPasswordRecovery: (email: string) => Promise<void>;
  readonly updateRecoveredPassword: (password: string) => Promise<void>;
  readonly cancelPasswordRecovery: () => void;
  readonly children: ReactNode;
}) {
  const applicationName = props.applicationName ?? 'CREST';
  const publicCompanyName = props.publicCompanyName ?? applicationName;
  const publicThemeStyle = {
    ...(props.publicBrandColor === undefined ? {} : { '--crest-public-brand': props.publicBrandColor }),
    ...(props.publicBrandStrongColor === undefined ? {} : {
      '--crest-public-brand-strong': props.publicBrandStrongColor,
    }),
    ...(props.publicAttentionColor === undefined ? {} : {
      '--crest-public-attention': props.publicAttentionColor,
    }),
  } as CSSProperties;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmedPassword, setConfirmedPassword] = useState('');
  const [formMode, setFormMode] = useState<'sign-in' | 'request-recovery'>('sign-in');
  const [recoveryRequested, setRecoveryRequested] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const applicationMark = applicationName
    .split(/\s+/)
    .filter((part) => part.length > 0)
    .slice(0, 2)
    .map((part) => part[0]!.toLocaleUpperCase())
    .join('') || 'CR';

  if (props.status === 'loading') {
    return (
      <main className="crest-public-theme crest-auth-loading" style={publicThemeStyle}>
        <span className="crest-auth-spinner" aria-hidden="true" />
        <p role="status" aria-live="polite">Checking your {applicationName} session…</p>
      </main>
    );
  }
  if (props.status === 'authenticated') return <>{props.children}</>;

  const resetLocalForm = () => {
    setPassword('');
    setConfirmedPassword('');
    setFailure(null);
    setSubmitting(false);
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const submittedValues = new FormData(event.currentTarget);
    const submittedEmail = submittedValues.get('email');
    const submittedPassword = submittedValues.get('password');
    setFailure(null);
    setSubmitting(true);
    try {
      await props.signInWithPassword(
        typeof submittedEmail === 'string' ? submittedEmail : '',
        typeof submittedPassword === 'string' ? submittedPassword : '',
      );
      setPassword('');
    } catch (error) {
      console.error(crestAuthFailureDiagnostic(error));
      setFailure('Sign-in failed. Check your email and password, then try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const requestRecovery = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const submittedValues = new FormData(event.currentTarget);
    const submittedEmail = submittedValues.get('email');
    setFailure(null);
    setSubmitting(true);
    try {
      await props.requestPasswordRecovery(typeof submittedEmail === 'string' ? submittedEmail : '');
      setRecoveryRequested(true);
    } catch (error) {
      console.error(crestAuthFailureDiagnostic(error));
      setFailure('The request could not be submitted. Check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const updatePassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const submittedValues = new FormData(event.currentTarget);
    const submittedPassword = submittedValues.get('password');
    const submittedConfirmation = submittedValues.get('password-confirmation');
    const nextPassword = typeof submittedPassword === 'string' ? submittedPassword : '';
    if (nextPassword !== submittedConfirmation) {
      setFailure('Passwords do not match.');
      return;
    }
    setFailure(null);
    setSubmitting(true);
    try {
      await props.updateRecoveredPassword(nextPassword);
      resetLocalForm();
    } catch (error) {
      console.error(crestAuthFailureDiagnostic(error));
      setFailure('The password could not be updated. Request a new recovery link and try again.');
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <main className="crest-public-theme crest-auth-screen" aria-labelledby="crest-auth-title"
      style={publicThemeStyle}>
      <section className="crest-auth-panel">
        <div className="crest-auth-brand">
          {props.publicLogoUrl == null ? (
            <span className="crest-auth-mark" aria-hidden="true">{applicationMark}</span>
          ) : (
            <img className="crest-auth-logo" src={props.publicLogoUrl} alt={publicCompanyName}
              referrerPolicy="no-referrer" />
          )}
          <span>{applicationName}</span>
        </div>
        <div className="crest-auth-form-wrap">
          {props.status === 'recovery' ? (
            <>
              <p className="crest-auth-eyebrow">Account recovery</p>
              <h1 id="crest-auth-title">Choose a new password</h1>
              <p className="crest-auth-intro">Enter a new password for your {applicationName} account.</p>
              <form className="crest-auth-form" onSubmit={(event) => void updatePassword(event)}>
                {failure === null ? null : <p className="crest-auth-error" role="alert">{failure}</p>}
                <label className="crest-auth-field" htmlFor="crest-recovery-password">
                  <span>New password</span>
                  <input id="crest-recovery-password" name="password" type="password" autoComplete="new-password"
                    minLength={8} maxLength={128} value={password}
                    onChange={(event) => setPassword(event.target.value)} required disabled={submitting} />
                </label>
                <label className="crest-auth-field" htmlFor="crest-recovery-password-confirmation">
                  <span>Confirm new password</span>
                  <input id="crest-recovery-password-confirmation" name="password-confirmation" type="password"
                    autoComplete="new-password" minLength={8} maxLength={128} value={confirmedPassword}
                    onChange={(event) => setConfirmedPassword(event.target.value)} required disabled={submitting} />
                </label>
                <p className="crest-auth-help">Use 8 to 128 characters.</p>
                <button className="crest-auth-submit" type="submit" disabled={submitting}>
                  {submitting ? 'Updating password…' : 'Update password'}
                </button>
              </form>
            </>
          ) : props.status === 'recovery-error' ? (
            <>
              <p className="crest-auth-eyebrow">Account recovery</p>
              <h1 id="crest-auth-title">That link is no longer valid</h1>
              <p className="crest-auth-intro" role="alert">
                The recovery link is invalid, expired, or has already been used. Request a new link to continue.
              </p>
              <button className="crest-auth-submit" type="button" onClick={() => {
                resetLocalForm();
                setRecoveryRequested(false);
                setFormMode('request-recovery');
                props.cancelPasswordRecovery();
              }}>Request a new link</button>
            </>
          ) : formMode === 'request-recovery' ? (
            <>
              <p className="crest-auth-eyebrow">Account recovery</p>
              <h1 id="crest-auth-title">Reset your password</h1>
              <p className="crest-auth-intro">Enter your assigned account email.</p>
              {recoveryRequested ? (
                <p className="crest-auth-success" role="status" aria-live="polite">
                  If an account matches that email, a recovery link will arrive shortly.
                </p>
              ) : (
                <form className="crest-auth-form" onSubmit={(event) => void requestRecovery(event)}>
                  {failure === null ? null : <p className="crest-auth-error" role="alert">{failure}</p>}
                  <label className="crest-auth-field" htmlFor="crest-recovery-email">
                    <span>Email address</span>
                    <input id="crest-recovery-email" name="email" type="email" autoComplete="email" value={email}
                      onChange={(event) => setEmail(event.target.value)} required disabled={submitting} />
                  </label>
                  <button className="crest-auth-submit" type="submit" disabled={submitting}>
                    {submitting ? 'Submitting request…' : 'Send recovery link'}
                  </button>
                </form>
              )}
              <button className="crest-auth-text-action" type="button" onClick={() => {
                resetLocalForm();
                setRecoveryRequested(false);
                setFormMode('sign-in');
              }}>Back to sign in</button>
            </>
          ) : (
            <>
              <p className="crest-auth-eyebrow">Secure operations workspace</p>
              <h1 id="crest-auth-title">Welcome back</h1>
              <p className="crest-auth-intro">Sign in to {applicationName} with your assigned account.</p>
              {props.status === 'recovery-complete' ? (
                <p className="crest-auth-success" role="status">Password updated. Sign in with your new password.</p>
              ) : null}
              {props.status === 'password-change-complete' ? (
                <p className="crest-auth-success" role="status">Password changed. Sign in with your new password.</p>
              ) : null}
              {props.status === 'password-change-unconfirmed' ? (
                <p className="crest-auth-error" role="alert">
                  We could not confirm whether your password changed. Sign in again before trying another change.
                </p>
              ) : null}
              <form className="crest-auth-form" onSubmit={(event) => void submit(event)}>
                {failure === null ? null : <p className="crest-auth-error" role="alert">{failure}</p>}
                <label className="crest-auth-field" htmlFor="crest-sign-in-email">
                  <span>Email address</span>
                  <input id="crest-sign-in-email" name="email" type="email" autoComplete="email" value={email}
                    onChange={(event) => setEmail(event.target.value)} required disabled={submitting} />
                </label>
                <label className="crest-auth-field" htmlFor="crest-sign-in-password">
                  <span>Password</span>
                  <input id="crest-sign-in-password" name="password" type="password"
                    autoComplete="current-password" value={password}
                    onChange={(event) => setPassword(event.target.value)} required disabled={submitting} />
                </label>
                <button className="crest-auth-submit" type="submit" disabled={submitting}>
                  {submitting ? 'Signing in…' : 'Sign in'}
                </button>
              </form>
              <button className="crest-auth-text-action" type="button" onClick={() => {
                resetLocalForm();
                setFormMode('request-recovery');
              }}>Forgot password?</button>
            </>
          )}
        </div>
        <p className="crest-auth-footer">{publicCompanyName} · Internal use only</p>
      </section>
      <aside className="crest-auth-visual" aria-hidden="true">
        {props.publicHeroImageUrl == null ? null : (
          <img className="crest-auth-hero-image" src={props.publicHeroImageUrl} alt=""
            referrerPolicy="no-referrer" />
        )}
        <span className="crest-auth-hero-overlay" />
        <div className="crest-auth-visual-content">
          <span className="crest-auth-visual-kicker">Built for the workday</span>
          <h2>One clear place to run the job.</h2>
          <p>Projects, schedules, tasks, files, photos, and customer follow-through—kept together.</p>
          <div className="crest-auth-work-preview">
            <span>Projects</span>
            <span>Schedule</span>
            <span>Files &amp; photos</span>
          </div>
        </div>
      </aside>
    </main>
  );
}

export function CrestSignOutButton() {
  const auth = useCrestAuth();
  return (
    <button className="crest-account-action" type="button" onClick={() => void auth.signOut()}
      aria-label={`Sign out of ${auth.applicationName}`}>
      <span aria-hidden="true">↗</span>
      <span>Sign out</span>
    </button>
  );
}
